import styles from './TransferToOtherDistrictTable.module.css';
import { Box, Button, Drawer, IconButton, MenuItem, Portal, Select, TextField, Typography } from '@mui/material';
import AppStripedDataGrid from 'src/components/AppStripedDataGrid';
import { GridColDef, GridToolbar, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { EmployeesService, GetEmployees_Out, GetMultipleEmployees_Out } from 'src/pehra-ts-client';
import { useCallback, useState } from 'react';
import EmployeeDetailedView from 'src/pages/Employees/components/EmployeeDetailedView';
import TransferToOfficeModal from 'src/pages/Employees/components/TransferToOfficeModal';
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from 'src/hooks/useNotification';
import { LoadingButton } from '@mui/lab';

export interface ITransferToOtherDistrictTable {
  rows: GetEmployees_Out[] | undefined;
}

const getBgColor = (state: string) => {
  switch (state) {
    case 'TRANSFERRED_TO_ANOTHER_DISTRICT':
      return '#ffebb3';
    case 'ACTIVE_IN_PRESENT_OFFICE':
      return '#9be8d6';
    case 'LOCKED':
      return 'red';
    default:
      return 'grey';
  }
};

function MyCustomToolbar(props: any) {
  return (
    <React.Fragment>
      <GridToolbar {...props} />
    </React.Fragment>
  );
}

const TransferToOtherDistrictTable: React.FC<ITransferToOtherDistrictTable> = ({ rows }) => {
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [openTransferModal, setOpenTransferModal] = useState<boolean>(false);
    const [selectedEmployee, setSelectedEmployee] = useState<GetEmployees_Out | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<string>('');
    const [filterValue, setFilterValue] = useState<string>('');
    const [appliedFilters, setAppliedFilters] = useState<{ field: string; value: string }[]>([]);
    const [selectedRows, setSelectedRows] = useState<any>([]);
  
    const queryClient = useQueryClient();
  
    const [sendNotification] = useNotification();
  
    const handleOpenTransferModal = () => {
      setOpenTransferModal(true);
    };
  
    const HIDDEN_FIELDS = [
      {
        label: "Father's Name",
        name: 'father_name',
        type: 'string',
        required: false
      },
      {
        label: 'Date of Birth',
        name: 'date_of_birth',
        type: 'date',
        required: false
      },
      {
        label: 'Date of Appointment',
        name: 'date_of_appointment',
        type: 'date',
        required: false
      },
      {
        label: 'Type of Appointment',
        name: 'type_of_appointment',
        type: 'string',
        required: false
      },
      {
        label: 'Gender',
        name: 'gender',
        type: 'string',
        required: false
      },
      {
        label: 'Caste',
        name: 'caste',
        type: 'string',
        required: false
      },
      {
        label: 'Category',
        name: 'category',
        type: 'string',
        required: false
      },
      {
        label: 'Education',
        name: 'education',
        type: 'string',
        required: false
      },
      {
        label: 'Special Qualifications',
        name: 'special_qualifications',
        type: 'string',
        required: false
      },
      {
        label: 'Home District',
        name: 'home_district',
        type: 'string',
        required: false
      },
      {
        label: 'Current Posting Role',
        name: 'current_posting_role',
        type: 'string',
        required: false
      },
      {
        label: 'Mobile',
        name: 'mobile',
        type: 'string',
        required: false
      },
      {
        label: 'Rank Start Date',
        name: 'rank_start_date',
        type: 'date',
        required: false
      },
      {
        label: 'Employee Type',
        name: 'employee_type',
        type: 'string',
        required: false
      },
      {
        label: 'Date of Agra Posting',
        name: 'date_of_agra_posting',
        type: 'date',
        required: false
      },
      {
        label: 'Date of Posting to Zone',
        name: 'date_of_posting_to_zone',
        type: 'date',
        required: false
      },
      {
        label: 'Previous Postings',
        name: 'previous_postings',
        type: 'string',
        required: false
      },
      {
        label: 'Punishments',
        name: 'punishments',
        type: 'string',
        required: false
      },
      {
        label: 'Other Details',
        name: 'other_details',
        type: 'string',
        required: false
      }
    ];
  
    const columnsEmployeeTable: GridColDef[] = [
      {
        field: 'Action',
        headerClassName: 'table--header',
        headerName: '',
        width: 100,
        renderCell: (params: any) => {
          return (
            <Box className={styles.rowDiv}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => {
                  setSelectedEmployee(params.row);
                  setOpenDrawer(true);
                }}
              >
                View
              </Button>
            </Box>
          );
        }
      },
      {
        field: 'badge_number',
        headerClassName: 'table--header',
        headerName: 'Badge Number',
        width: 100,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.badge_number}</Box>;
        }
      },
      {
        field: 'rank',
        headerClassName: 'table--header',
        headerName: 'Rank',
        width: 100,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.rank || 'N/A'}</Box>;
        }
      },
      {
        field: 'pno_number',
        headerClassName: 'table--header',
        headerName: 'PNO Number',
        width: 100,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.pno_number}</Box>;
        }
      },
      {
        field: 'ehrms_code',
        headerClassName: 'table--header',
        headerName: 'EHRMS Code',
        width: 100,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.ehrms_code}</Box>;
        }
      },
      {
        field: 'name',
        headerClassName: 'table--header',
        headerName: 'Name',
        width: 200,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.name}</Box>;
        }
      },
      {
        field: 'current_office_name',
        headerClassName: 'table--header',
        headerName: 'Current Office',
        width: 100,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.current_office_name}</Box>;
        }
      },
      {
        field: 'current_position',
        headerClassName: 'table--header',
        headerName: 'Current Position',
        width: 200,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.current_position}</Box>;
        }
      },
      {
        field: 'date_of_current_position',
        headerClassName: 'table--header',
        headerName: 'Date of Current Position',
        width: 200,
        renderCell: (params: any) => {
          return <Box className={styles.rowDiv}>{params.row.date_of_current_position}</Box>;
        }
      },
      {
        field: 'state',
        headerClassName: 'table--header',
        headerName: 'State',
        width: 300,
        renderCell: (params: any) => {
          return (
            <Box className={styles.rowDiv}>
              <Box
                sx={{
                  backgroundColor: getBgColor(params.row.state),
                  padding: '6px',
                  borderRadius: '15px'
                }}
              >
                {params.row.state}
              </Box>
            </Box>
          );
        }
      }
    ];
  
    const hiddenColumns = HIDDEN_FIELDS.map((field) => {
      return {
        field: field.name,
        headerClassName: 'table--header',
        headerName: field.label,
        width: 150,
        hide: true

      };
    });
  
    columnsEmployeeTable.push(...hiddenColumns);
  
    const handleSelectionChange = (selectionModel: any) => {
      setSelectedRows(selectionModel);
    };
  
    const initialState = {
      columns: {
        columnVisibilityModel: HIDDEN_FIELDS.reduce((acc: any, field: any) => {
          acc[field.name] = false;
          return acc;
        }, {})
      }
    };
  
    const transferToOffice = async () => {
      try {
        const res = await EmployeesService.moveEmployeeToTransferState({
          employee_ids: selectedRows
        });
        return res;
      } catch (error) {
        throw error;
      }
    };
  
    const transferToOfficeMutation = useMutation({
      mutationFn: transferToOffice,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['employeesList']
        });
        sendNotification({
          msg: 'Employees added to transfer pool',
          type: 'success'
        });
      }
    });
  
    const handleMoveToCommonTransferPool = () => {
      transferToOfficeMutation.mutate();
    };
  
    return (
      <Box className={styles.tableContainer}>
        <Box id="filter-panel"></Box>
        <Box
          sx={{
            marginTop: '10px',
            marginBottom: '10px',
            background: 'transparent'
          }}
        >
          {selectedRows.length > 0 && (
            <Box className={styles.selectedRowsActionContainer}>
              <Typography variant="h6" className={styles.selectedRowsTitle}>
                {selectedRows.length} Selected
              </Typography>
              <LoadingButton
                variant="outlined"
                onClick={handleMoveToCommonTransferPool}
                loading={transferToOfficeMutation.isPending}
                loadingIndicator="Saving..."
              >
                Move to Common Transfer Pool
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenTransferModal(true);
                }}
              >
                Move to Office Transfer Pool
              </Button>
            </Box>
          )}
        </Box>
        <AppStripedDataGrid
          autoHeight
          columns={columnsEmployeeTable}
          rows={rows || []}
          style={{
            width: '100%'
          }}
          getRowId={(row: any) => row.id}
          slots={{
            toolbar: MyCustomToolbar
          }}
          checkboxSelection
          onRowSelectionModelChange={(newSelection: any) => {
            console.log(newSelection);
            handleSelectionChange(newSelection);
          }}
          initialState={{ initialState, pagination: { paginationModel: { pageSize: 50 } } }}
          disableRowSelectionOnClick
        />
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
            setSelectedEmployee(null);
          }}
          PaperProps={{
            sx: { width: '60%', padding: '20px 0 0 20px' }
          }}
        >
          {selectedEmployee ? (
            <EmployeeDetailedView employeeDetail={selectedEmployee} handleOpenTransferModal={handleOpenTransferModal} />
          ) : null}
        </Drawer>
        <TransferToOfficeModal
          openModal={openTransferModal}
          handleCloseModal={() => setOpenTransferModal(false)}
          employeesList={selectedRows}
        />
      </Box>
    );
  };

export default TransferToOtherDistrictTable;
